import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Calendar, MapPin, Trophy, ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getMatches, getNews, getStandings } from "@/lib/api";
import { formatDateShort, formatTime } from "@/lib/dateUtils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";
const HERO_IMAGE = "./assets/images/bg-banner-futsal-gent.png";

export default function Home() {
  const [upcomingMatch, setUpcomingMatch] = useState(null);
  const [recentMatch, setRecentMatch] = useState(null);
  const [news, setNews] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchesRes, newsRes, standingsRes] = await Promise.all([
          getMatches(),
          getNews({ limit: 3 }),
          getStandings({ category: "EERSTE" }),
        ]);

        const matches = matchesRes.data;
        const upcoming = matches.find((m) => m.status === "upcoming");
        const completed = matches.filter((m) => m.status === "completed");
        
        setUpcomingMatch(upcoming);
        setRecentMatch(completed[0]);
        setNews(newsRes.data);
        setStandings(standingsRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16">
          <div className="animate-fade-in-up">
            <Badge className="badge-gold mb-4">VOLGENDE WEDSTRIJD</Badge>
            
            {upcomingMatch ? (
              <div className="bg-slate-950/60 backdrop-blur-md rounded-lg p-6 md:p-8 max-w-2xl border border-white/10">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDateShort(upcomingMatch.match_date)}</span>
                    <Clock className="h-4 w-4 ml-2" />
                    <span>{upcomingMatch.match_time}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between gap-4">
                  <div className="text-center flex-1">
                    <img 
                      src={LOGO_URL} 
                      alt={upcomingMatch.home_team} 
                      className="h-16 w-16 mx-auto mb-2 object-contain"
                    />
                    <p className="font-bold text-white text-sm md:text-base">
                      {upcomingMatch.home_team}
                    </p>
                  </div>
                  
                  <div className="text-center px-4">
                    <span className="text-3xl md:text-4xl font-black text-[hsl(48,96%,53%)] font-['Chivo']">VS</span>
                  </div>
                  
                  <div className="text-center flex-1">
                    {upcomingMatch.away_team_logo_url ? (
                      <img 
                        src={upcomingMatch.away_team_logo_url} 
                        alt={upcomingMatch.away_team} 
                        className="h-16 w-16 mx-auto mb-2 object-contain"
                      />
                    ) : (
                      <div className="h-16 w-16 mx-auto mb-2 bg-slate-800 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-slate-400">
                          {upcomingMatch.away_team.charAt(0)}
                        </span>
                      </div>
                    )}
                    <p className="font-bold text-white text-sm md:text-base">
                      {upcomingMatch.away_team}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mt-4 text-slate-400 text-sm justify-center">
                  <MapPin className="h-4 w-4" />
                  <span>{upcomingMatch.venue}</span>
                </div>
              </div>
            ) : (
              <div className="bg-slate-950/60 backdrop-blur-md rounded-lg p-8 max-w-md border border-white/10">
                <p className="text-slate-400">Geen aankomende wedstrijden gepland</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bento Grid Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* Latest Result - Large Card */}
          <Card className={`lg:col-span-2 card-hover animate-fade-in-up ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`} data-testid="recent-match-card">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-sm font-medium uppercase tracking-widest ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Laatste Resultaat</h3>
                <Badge variant="outline" className={isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}>
                  {recentMatch?.competition || "Belgian Futsal League"}
                </Badge>
              </div>
              
              {recentMatch ? (
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <img 
                      src={LOGO_URL} 
                      alt={recentMatch.home_team} 
                      className="h-20 w-20 mx-auto mb-3 object-contain"
                    />
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{recentMatch.home_team}</p>
                  </div>
                  
                  <div className="text-center px-8">
                    <div className={`score-display ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      <span className={recentMatch.home_score > recentMatch.away_score ? "text-[hsl(48,96%,53%)]" : ""}>
                        {recentMatch.home_score}
                      </span>
                      <span className={isDark ? 'text-slate-500' : 'text-slate-400'}> - </span>
                      <span className={recentMatch.away_score > recentMatch.home_score ? "text-[hsl(48,96%,53%)]" : ""}>
                        {recentMatch.away_score}
                      </span>
                    </div>
                    <p className={`text-sm mt-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{formatDateShort(recentMatch.match_date)}</p>
                  </div>
                  
                  <div className="text-center flex-1">
                    {recentMatch.away_team_logo_url ? (
                      <img 
                        src={recentMatch.away_team_logo_url} 
                        alt={recentMatch.away_team} 
                        className="h-20 w-20 mx-auto mb-3 object-contain"
                      />
                    ) : (
                      <div className={`h-20 w-20 mx-auto mb-3 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                        <span className={`text-3xl font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {recentMatch.away_team.charAt(0)}
                        </span>
                      </div>
                    )}
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{recentMatch.away_team}</p>
                  </div>
                </div>
              ) : (
                <p className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Geen recente wedstrijden</p>
              )}
              
              <div className="mt-6 flex justify-center">
                <Button asChild variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}>
                  <Link to="/games" data-testid="view-all-games">
                    Bekijk Alle Wedstrijden <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* League Table - Tall Card */}
          <Card className={`card-hover animate-fade-in-up stagger-1 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`} data-testid="standings-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-sm font-medium uppercase tracking-widest ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Ranglijst</h3>
                <Trophy className="h-5 w-5 text-[hsl(48,96%,53%)]" />
              </div>
              
              <div className="space-y-3">
                {standings.map((team, index) => (
                  <div 
                    key={team.id || index}
                    className={`flex items-center gap-3 p-2 rounded ${
                      team.team_name === "Futsal Gent" 
                        ? "bg-blue-500/20 border border-blue-500/30" 
                        : ""
                    }`}
                  >
                    <span className={`w-6 text-center font-bold ${
                      index === 0 ? "text-[hsl(48,96%,53%)]" : isDark ? "text-slate-500" : "text-slate-400"
                    }`}>
                      {team.position}
                    </span>
                    <span className={`flex-1 font-medium truncate ${
                      team.team_name === "Futsal Gent" ? "text-blue-400" : isDark ? "text-white" : "text-slate-900"
                    }`}>
                      {team.team_name}
                    </span>
                    <span className={`font-bold w-8 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>{team.points}</span>
                  </div>
                ))}
              </div>
              
              <Button asChild variant="ghost" className={`w-full mt-4 ${isDark ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}>
                <Link to="/ranking" data-testid="view-full-standings">
                  Volledige Ranglijst <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* News Cards */}
          {news.map((article, index) => (
            <Card 
              key={article.id} 
              className={`card-hover news-card overflow-hidden animate-fade-in-up ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}
              style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              data-testid={`news-card-${index}`}
            >
              {article.image_url && (
                <div className="h-40 overflow-hidden">
                  <img 
                    src={article.image_url} 
                    alt={article.title}
                    className="w-full h-full object-cover news-image"
                  />
                </div>
              )}
              <CardContent className="p-5">
                <Badge variant="outline" className={`mb-2 ${isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}`}>
                  {article.category}
                </Badge>
                <h4 className={`font-bold mb-2 line-clamp-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{article.title}</h4>
                <p className={`text-sm line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{article.summary}</p>
                <Link 
                  to={`/news/${article.id}`}
                  className="inline-flex items-center text-[hsl(217,91%,60%)] text-sm mt-3 hover:text-[hsl(48,96%,53%)] transition-colors"
                  data-testid={`read-more-${index}`}
                >
                  Lees Meer <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* View All News Button */}
        <div className="text-center mt-8">
          <Button asChild className="bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)] btn-primary">
            <Link to="/news" data-testid="view-all-news">
              Bekijk Alle Nieuws <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Call to Action */}
      <section className={`border-y py-16 ${isDark ? 'bg-gradient-to-r from-blue-600/20 to-transparent border-white/10' : 'bg-gradient-to-r from-blue-50 to-transparent border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-3xl md:text-4xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Word Deel van de Futsal Gent Familie
          </h2>
          <p className={`mb-8 max-w-2xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Volg onze reis terwijl we op het hoogste niveau van Belgisch futsal strijden. 
            Word deel van onze gepassioneerde gemeenschap.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild className="btn-gold">
              <Link to="/contact" data-testid="contact-cta">Neem Contact Op</Link>
            </Button>
            <Button asChild variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-300 text-slate-700 hover:bg-slate-100'}>
              <Link to="/club" data-testid="about-cta">Meer Informatie</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
